'use client';

import { useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Send, MessagesSquare } from 'lucide-react';
import { useCreateSupportTicket, useSupportTickets } from '../hooks/use-support';
import { useSupportContext } from '../hooks/use-support-context';

interface NewTicketFormProps {
  onBack: () => void;
  onCreated: (ticketId: string) => void;
  initialSubject?: string;
}

export function NewTicketForm({
  onBack,
  onCreated,
  initialSubject = '',
}: NewTicketFormProps) {
  const create = useCreateSupportTicket();
  const { snapshot } = useSupportContext();
  const { data: ticketsData } = useSupportTickets();
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState('');

  // Daca user-ul are o conversatie deschisa, mesajul nou se va adauga la ea
  // (auto-grupare in backend). Avertizam clar ca sa stie.
  const activeTicket = useMemo(
    () =>
      ticketsData?.data?.find(
        (t) =>
          t.status === 'open' ||
          t.status === 'pending_user' ||
          t.status === 'pending_agent',
      ) ?? null,
    [ticketsData?.data],
  );

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const trimmedBody = body.trim();
    if (!trimmedBody) return;
    // Subiectul conteaza doar daca e tichet nou. Pentru continuare, backend-ul
    // pastreaza subiectul existent si ignora ce trimitem.
    const subjectToSend = activeTicket
      ? activeTicket.subject
      : subject.trim() || '(fara subiect)';
    try {
      const ticket = await create.mutateAsync({
        subject: subjectToSend,
        body: trimmedBody,
        contextData: snapshot() as unknown as Record<string, unknown>,
      });
      onCreated(ticket.id);
    } catch {
      /* error surfaced below */
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
        <button
          type="button"
          onClick={onBack}
          aria-label="Înapoi"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
        </button>
        <div className="text-[0.875rem] font-medium text-neutral-900 dark:text-neutral-100">
          {activeTicket ? 'Continuă conversația' : 'Tichet nou'}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {activeTicket && (
          <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-900/40 dark:bg-amber-950/20">
            <MessagesSquare
              className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400"
              strokeWidth={1.75}
            />
            <div className="flex-1 text-[0.75rem] leading-relaxed text-amber-900 dark:text-amber-200">
              <strong className="font-semibold">Ai o conversație deschisă</strong> —
              mesajul tău se va adăuga la
              <span className="ml-1 font-medium">„{activeTicket.subject}"</span>,
              ca să nu se piardă firul. Echipa va vedea tot istoricul împreună.
            </div>
          </div>
        )}

        {!activeTicket && (
          <label className="mb-3 block">
            <span className="mb-1 block text-[0.6875rem] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500">
              Subiect
            </span>
            <input
              type="text"
              required
              maxLength={200}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex. Nu pot emite o factură"
              className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-[0.8125rem] text-neutral-900 placeholder:text-neutral-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100 focus-visible:ring-2 focus-visible:ring-amber-100 focus-visible:ring-offset-0 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-600 dark:focus:border-amber-500 dark:focus:ring-amber-950 dark:focus-visible:ring-amber-950"
            />
          </label>
        )}

        <label className="block">
          <span className="mb-1 block text-[0.6875rem] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500">
            Mesaj
          </span>
          <textarea
            required
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={
              activeTicket
                ? 'Adaugă un mesaj nou la conversație…'
                : 'Descrie ce ai încercat și ce s-a întâmplat. Atașează un screenshot dacă ajută.'
            }
            className="w-full resize-none rounded-lg border border-neutral-200 bg-white px-3 py-2 text-[0.8125rem] leading-relaxed text-neutral-900 placeholder:text-neutral-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100 focus-visible:ring-2 focus-visible:ring-amber-100 focus-visible:ring-offset-0 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-600 dark:focus:border-amber-500 dark:focus:ring-amber-950 dark:focus-visible:ring-amber-950"
          />
        </label>

        <div className="mt-3 rounded-lg bg-neutral-50 px-3 py-2 text-[0.6875rem] text-neutral-500 dark:bg-neutral-900 dark:text-neutral-500">
          Atașăm automat pagina curentă, browserul și firma — ne ajută să-ți răspundem fără
          întrebări suplimentare.
        </div>

        {create.isError && (
          <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-[0.75rem] text-red-700 dark:bg-red-950/30 dark:text-red-400">
            Tichetul nu s-a putut crea. Încearcă din nou sau scrie-ne pe email.
          </div>
        )}
      </div>

      <div className="border-t border-neutral-200 p-3 dark:border-neutral-800">
        <button
          type="submit"
          disabled={(!activeTicket && !subject.trim()) || !body.trim() || create.isPending}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-[0.8125rem] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(245,158,11,0.55)] transition-all hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-400 disabled:shadow-none dark:disabled:bg-neutral-800 dark:disabled:text-neutral-600"
        >
          {create.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" strokeWidth={1.75} />
          )}
          {activeTicket ? 'Trimite mesajul' : 'Trimite tichetul'}
        </button>
      </div>
    </form>
  );
}
